import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmployeeAvatar, Grid, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { Employee, EmployeesMetadata } from '../types/Employee';
import { getEmployees, getEmployeesMetadata } from '../services/employeeService';
import gridStyles from '../../../shared/components/Grid.module.css';
import styles from './TeamMembers.module.css';

type SortField = 'name' | 'email' | 'title' | 'isActive' | 'lastLoginAt';
type SortDirection = 'asc' | 'desc';
type ActiveFilter = 'all' | 'active' | 'inactive';

const parseSortField = (value: string | null): SortField => {
  if (value === 'name' || value === 'email' || value === 'title' || value === 'isActive' || value === 'lastLoginAt') {
    return value;
  }

  return 'name';
};

const parseSortDirection = (value: string | null): SortDirection => {
  if (value === 'asc' || value === 'desc') {
    return value;
  }

  return 'asc';
};

const parseActiveFilter = (value: string | null): ActiveFilter => {
  if (value === 'active' || value === 'inactive' || value === 'all') {
    return value;
  }

  return 'all';
};

const getEmployeeSubtitle = (employee: Employee): string => {
  return employee.positionName || employee.title || employee.contractTypeName || employee.email || '-';
};

export const TeamMembers: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<EmployeesMetadata | null>(null);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [sortField, setSortField] = useState<SortField>(() => parseSortField(searchParams.get('sortBy')));
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => parseSortDirection(searchParams.get('sortDir')));
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(() => parseActiveFilter(searchParams.get('active')));
  const [locationFilter, setLocationFilter] = useState(() => searchParams.get('locationId') || '');

  const isActiveQuery = useMemo(() => {
    if (activeFilter === 'active') return true;
    if (activeFilter === 'inactive') return false;
    return undefined;
  }, [activeFilter]);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEmployees({
        q: searchTerm,
        isActive: isActiveQuery,
        locationId: locationFilter || undefined,
        sortBy: sortField,
        sortDir: sortDirection,
        page: 1,
        pageSize: 100,
      });
      setEmployees(data);
      setError(null);
    } catch {
      setError(t('failedToLoadTeamMembers', { ns: 'admin' }));
    } finally {
      setLoading(false);
    }
  }, [searchTerm, isActiveQuery, locationFilter, sortField, sortDirection, t]);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const data = await getEmployeesMetadata();
        setMetadata(data);
      } catch {
        // Metadata is optional for list rendering; keep core list usable.
      }
    };

    loadMetadata();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadEmployees();
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadEmployees]);

  useEffect(() => {
    const next = new URLSearchParams();

    const q = searchTerm.trim();
    if (q) {
      next.set('q', q);
    }

    if (activeFilter !== 'all') {
      next.set('active', activeFilter);
    }

    if (locationFilter) {
      next.set('locationId', locationFilter);
    }

    if (sortField !== 'name') {
      next.set('sortBy', sortField);
    }

    if (sortDirection !== 'asc') {
      next.set('sortDir', sortDirection);
    }

    const current = searchParams.toString();
    const updated = next.toString();
    if (current !== updated) {
      setSearchParams(next, { replace: true });
    }
  }, [searchTerm, activeFilter, locationFilter, sortField, sortDirection, searchParams, setSearchParams]);

  const onSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loadingTeamMembers', { ns: 'admin' })} />
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{t('teamMembers', { ns: 'admin' })}</h2>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.searchWrap}>
            <input
              className={`search-input ${styles.searchInput}`}
              type="text"
              placeholder={t('searchTeamMembersPlaceholder', { ns: 'admin' })}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className={`text-input ${styles.statusSelect}`}
            value={activeFilter}
            onChange={e => setActiveFilter(e.target.value as ActiveFilter)}
          >
            <option value="all">{t('allStatus', { ns: 'admin' })}</option>
            <option value="active">{t('active', { ns: 'admin' })}</option>
            <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
          </select>

          <select
            className={`text-input ${styles.statusSelect}`}
            value={locationFilter}
            onChange={e => setLocationFilter(e.target.value)}
          >
            <option value="">{t('allLocations', { ns: 'admin' })}</option>
            {(metadata?.locations || []).map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </div>

        <button className={`btn btn-primary ${styles.addButton}`} type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate('/members/add')}>
          <span aria-hidden>＋</span>
          <span>{t('add', { ns: 'common' })}</span>
        </button>
      </div>

      {employees.length === 0 ? (
        <div className={styles.emptyState}>{t('noEmployeesFound', { ns: 'admin' })}</div>
      ) : (
      <Grid
        columns={[
          {
            key: 'name',
            label: t('name', { ns: 'admin' }),
            sortable: true,
            className: styles.nameColumn,
            headerClassName: styles.nameColumn,
            render: (employee: Employee) => {
              return (
                <div className={styles.nameValue}>
                  <EmployeeAvatar
                    name={employee.name}
                    imageUrl={employee.profileImageUrl}
                    colorPreference={employee.colorPreference}
                    className={styles.avatar}
                    size={38}
                  />
                  <div className={styles.nameTextBlock}>
                    <span className={styles.primaryText}>{employee.name || '-'}</span>
                    <span className={styles.secondaryText}>{getEmployeeSubtitle(employee)}</span>
                  </div>
                </div>
              );
            },
          },
          {
            key: 'email',
            label: t('email', { ns: 'admin' }),
            sortable: true,
            className: styles.emailColumn,
            headerClassName: styles.emailColumn,
            render: (employee: Employee) => employee.email
              ? <span className={`${styles.valueText} ${styles.interactiveValue}`}>{employee.email}</span>
              : <span className={`${styles.valueText} ${styles.mutedValue}`}>-</span>,
            onCellClick: (employee: Employee) => {
              if (employee.email) {
                window.location.href = `mailto:${employee.email}`;
              }
            }
          },
          {
            key: 'title',
            label: t('title', { ns: 'admin' }),
            sortable: true,
            className: styles.titleColumn,
            headerClassName: styles.titleColumn,
            render: (employee: Employee) => <span className={`${styles.valueText} ${employee.positionName || employee.title ? '' : styles.mutedValue}`}>{employee.positionName || employee.title || '-'}</span>
          },
          {
            key: 'isActive',
            label: t('status', { ns: 'admin' }),
            sortable: true,
            className: `${gridStyles.small} ${styles.compactColumn}`,
            headerClassName: gridStyles.small,
            render: (employee: Employee) => (
              <span className={`${styles.statusBadge} ${employee.isActive ? styles.statusActive : styles.statusInactive}`}>
                {employee.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
              </span>
            ),
          },
          {
            key: 'lastLoginAt',
            label: t('lastLogin', { ns: 'admin' }),
            sortable: true,
            className: styles.lastLoginColumn,
            headerClassName: styles.lastLoginColumn,
            render: (employee: Employee) => (
              <span className={`${styles.valueText} ${employee.lastLoginAt ? '' : styles.mutedValue}`}>
                {employee.lastLoginAt ? formatDateTime(employee.lastLoginAt) : '-'}
              </span>
            ),
          },
        ]}
        rows={employees}
        rowKey={(employee: Employee) => employee.id}
        onRowClick={(employee: Employee) => navigate(`/members/${employee.id}`)}
        sortKey={sortField}
        sortDirection={sortDirection}
        onSort={(key) => onSort(key as SortField)}
      />
      )}
    </div>
  );
};

export default TeamMembers;